/* eslint-disable @typescript-eslint/naming-convention */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import LinearProgressDisplay from '@brands/Components/ProgressDisplay/ProgressDisplay';
import { MedicalHistorySubmit } from '@brands/Dashboard/MedicalHistory/MedicalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import MuiToggle from '../../../../../Components/Inputs/MuiToggle/MuiToggle';
import closeIcon from '../Icons/selectPatientClose.svg';
import logo from '../Icons/selectPatientLogo.svg';
import styles from './styles.module.scss';

const ClinicalDataStepTwo = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { state } = useLocation();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [underCare, setUnderCare] = useState<boolean | null>(null);
  const [historyObjectId, setHistoryObjectId] = useState<number | null>(null);
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>({
    id: 0,
    under_care_of_physician: false,
    conditions_treated: null,
    taking_medication: false,
    medications: null,
    allergic_to_medications: false,
    medications_allergies: null,
    using_substances: [],
    interested_in_stopping: null,
    height: '',
    weight: '',
    pharmacy_name: null,
    pharmacy_street_address: null,
    pharmacy_city: null,
    pharmacy_state: null,
    pharmacy_zip_code: null,
    pharmacy_phone_number: null,
  } as MedicalHistory);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);

      if (underCare) {
        roleNavigate('/clinical-data/step-three', {
          state: {
            caseType: values.find((item) => item.form_question_id === 20)?.value.caseType as string,
            isPatientForm: true,
            shouldSeeLastModal: state || '',
            patientId: selectedPatient?.value ? selectedPatient?.value : '',
          },
        });
        setLoading(false);
        return;
      }

      const medicalHistoryPayload: MedicalHistorySubmit = {
        user_id: selectedPatient?.value as number,
        ...medicalHistory,
        under_care_of_physician: false,
        conditions_treated: null,
      };

      if (!historyObjectId) {
        await addClinicalData('medical_history', medicalHistoryPayload);
      } else {
        await updateClinicalData('medical_history', historyObjectId.toString(), medicalHistoryPayload);
      }

      setLoading(false);
      roleNavigate('/clinical-data/step-four', {
        state: {
          caseType: values.find((item) => item.form_question_id === 20)?.value.caseType as string,
          isPatientForm: true,
          shouldSeeLastModal: state || '',
          patientId: selectedPatient?.value ? selectedPatient?.value : '',
        },
      });
    } catch (error) {
      displayErrorDetails(error);
    }
  };

  const backToDashboard = (): void => {
    roleNavigate('/dashboard');
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      const res = await listClinicalData(selectedPatient?.value);
      if (res.medical_history) {
        setUnderCare(res.medical_history.under_care_of_physician || false);
        setHistoryObjectId(res.medical_history.id || null);
        setMedicalHistory(res.medical_history);
      }

      setLoading(false);
    })();
  }, [selectedPatient]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <div className={styles.clinicalDataIntroTopDiv}>
          <div className={styles.clinicalDataIntroTopDivFirst}>
            <img src={logo} className={styles.clinicalDataIntroTopDivFirstLogo} alt="Logo" />
            <div onClick={backToDashboard}>
              <img src={closeIcon} className={styles.clinicalDataIntroTopDivFirstClose} alt="Close" />
            </div>
          </div>
          <LinearProgressDisplay progress={10} />
        </div>
        <form className={`${styles.formContainer}`}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroStep}>
                <span>MEDICAL HISTORY: 2 OF 9</span>
              </div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>Are you now, or have you been in the past year, under the care of a physician?</span>
                <span className={styles.clinicalDataIntroTitleRequired}> *</span>
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                <MuiToggle
                  label="Are you now, or have you been in the past year, under the care of a physician?"
                  hideLabel
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  selectedValue={underCare === null ? '' : underCare ? 'yes' : 'no'}
                  onChange={(val) => {
                    setUnderCare(val === 'yes');
                  }}
                />
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={loading || underCare === null}
                onClick={(e) => {
                  e.preventDefault();
                  if (underCare !== null) {
                    onSubmit();
                  } else {
                    showSnackbar('Please fill in all required fields before proceeding.', 'error');
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepTwo;
